'use client';

import { useEffect, useRef, useState } from 'react';
import { listStoreProducts } from '@/app/apis';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { CreditsPurchaseProduct, CreditV1CreditsPurchaseProduct } from '@/app/types';
import { useFetch } from './use-fetch';

export const formatCreditProducts = (products: CreditV1CreditsPurchaseProduct[]): CreditsPurchaseProduct[] =>
  products.map((product) => ({ ...product, product_pay_status: 'available' }));

interface UseFetchCreditProductsOptions {
  /**
   * 只在积分商品真正可见且用户具备访问资格时请求。
   * false -> true 时自动拉取一次；保持 true 时响应全局商品刷新信号。
   */
  enabled: boolean;
}

export const useFetchCreditProducts = ({ enabled }: UseFetchCreditProductsOptions) => {
  const { refreshGetProducts, updateRefreshGetProducts } = useStore((state) => ({
    refreshGetProducts: state.refreshGetProducts,
    updateRefreshGetProducts: state.updateRefreshGetProducts,
  }));
  const [creditsProducts, setCreditsProducts] = useState<CreditsPurchaseProduct[]>([]);
  const [responseError, setResponseError] = useState<Error>();
  const wasEnabled = useRef(false);

  const {
    loading,
    error: requestError,
    run: getCreditProducts,
  } = useFetch(
    () =>
      listStoreProducts({
        query: {
          channels: SITE === 'global' ? ['stripe'] : ['alipay'],
        },
      }),
    {
      manual: true,
      onBefore: () => {
        setResponseError(undefined);
        // 刷新期间不保留旧商品，避免优惠券锁定、商品下架或实验分组变化后仍可点击旧 SKU。
        setCreditsProducts([]);
      },
      onSuccess: ({ data, error }) => {
        if (error) {
          // HTTP 4xx/5xx 由生成客户端作为成功 Promise 中的 error 返回，
          // 单独转换为 Error，保证首次加载失败时页面能展示网络错误态。
          setResponseError(new Error(error.message));
          return;
        }
        setCreditsProducts(formatCreditProducts(data?.credits_purchase_products ?? []));
      },
    },
  );

  useEffect(() => {
    const becameEnabled = enabled && !wasEnabled.current;
    wasEnabled.current = enabled;

    if (!enabled || (!becameEnabled && !refreshGetProducts)) return;

    if (refreshGetProducts) {
      updateRefreshGetProducts(false);
    }
    getCreditProducts();
  }, [enabled, getCreditProducts, refreshGetProducts, updateRefreshGetProducts]);

  return {
    creditsProducts,
    error: responseError ?? requestError,
    getCreditProducts,
    loading,
  };
};
